import type { ReactNode } from 'react';

import { ErrorNotice } from '../../components/ErrorNotice';
import type { Panel } from './panel-state';

/**
 * The two presentational pieces the four quality panels share — CLM-40. The
 * state plumbing (`Panel`, `usePanel`, `usePaging`, `FORBIDDEN_COPY`) is in
 * `panel-state.ts`.
 */

/**
 * The problem branch every panel renders the same way: the notice, then the
 * per-kind sentence — the endpoint path for `not_implemented`, the role copy for
 * `forbidden`, a Retry for `unknown`.
 */
export function PanelProblem({
  panel,
  state,
  endpointPath,
  onRetry,
  forbiddenCopy,
}: {
  readonly panel: string;
  readonly state: Extract<Panel<never>, { kind: 'problem' }>;
  readonly endpointPath: string;
  readonly onRetry: () => void;
  /** `undefined` on panel (c): every role holds `contract:read`, so 403 is never expected there. */
  readonly forbiddenCopy?: string | undefined;
}) {
  let follow: ReactNode = null;
  if (state.problem === 'not_implemented') {
    follow = (
      <p className="mut">
        The endpoint is <code>GET {endpointPath}</code>. Until it answers, this panel makes no claim.
      </p>
    );
  } else if (state.problem === 'forbidden' && forbiddenCopy !== undefined) {
    follow = <p className="mut">{forbiddenCopy}</p>;
  }
  return (
    <ErrorNotice
      kind={state.problem}
      detail={state.detail}
      correlation_id={state.correlation_id}
      action={
        <>
          {follow}
          {state.problem === 'unknown' ? (
            <button type="button" className="btn sm" onClick={onRetry}>
              Retry {panel}
            </button>
          ) : null}
        </>
      }
    />
  );
}

/** One stat tile: a wire number under a label. `tone` is a display hint, never a computed figure. */
export function Tile({
  label,
  value,
  sub,
  tone,
}: {
  readonly label: string;
  readonly value: number | string;
  readonly sub?: string | undefined;
  readonly tone?: 'bad' | 'warn' | undefined;
}) {
  return (
    <div className="total" data-tone={tone ?? 'plain'}>
      <span>{label}</span>
      <b>{value}</b>
      {sub === undefined ? null : <small className="mut">{sub}</small>}
    </div>
  );
}
